import {Map, Record as IRecord, type RecordOf, List} from 'immutable';
import {
  stripeCurrencyList,
  paypalCurrencyList,
  enumOptionMapSets,
  enumOptionValueSets,
} from '@packages/systems/core/constants/SharedConfig';
import {
  SHIPPING_METHODS,
  INVENTORY_TYPE_FINITE,
  INVENTORY_TYPE_INFINITE,
} from '@packages/systems/commerce/constants';
import type {ImageRefType} from '@packages/systems/core/records/ImageRefRecord';
import type {CMSItemPublic} from '@packages/systems/dynamo/types';

// The values don't matter, only the keys. This is used
// to derive types, the orderEmailTypes array, and for
// determining if the emailType is a certain type
const _subscriptionEmailTypes = {
  paymentFailed: true,
  paymentSuccessful: true,
  subscriptionCanceled: true,
  verifyPayment: true,
} as const;
const _orderEmailTypes = {
  orderReceived: true,
  orderConfirmed: true,
  orderShipped: true,
  orderRefunded: true,
  orderDownloads: true,
} as const;
const _ecommerceEmailTypes = {
  ..._orderEmailTypes,
  ..._subscriptionEmailTypes,
} as const;

const _adminNotificationEmailTypes = {
  stripeChangedPlan: true,
  stripeChangedProduct: true,
  stripeChangedCustomer: true,
} as const;

export type EcommerceEmailType = keyof typeof _ecommerceEmailTypes;
export type OrderEmailType = keyof typeof _orderEmailTypes;
export type SubscriptionEmailType = keyof typeof _subscriptionEmailTypes;
export type AdminNotificationEmailType =
  keyof typeof _adminNotificationEmailTypes;
// @ts-expect-error - TS2322 - Type 'string[]' is not assignable to type 'readonly ("orderReceived" | "orderConfirmed" | "orderShipped" | "orderRefunded" | "orderDownloads")[]'.
export const orderEmailTypes: ReadonlyArray<OrderEmailType> =
  Object.keys(_orderEmailTypes);

export const isOrderEmailType = (emailType: EcommerceEmailType): boolean =>
  _orderEmailTypes.hasOwnProperty(emailType);

export const isSubscriptionEmailType = (
  emailType: EcommerceEmailType
): boolean => _subscriptionEmailTypes.hasOwnProperty(emailType);

export const isEcommerceEmailType = (emailType: EcommerceEmailType): boolean =>
  _ecommerceEmailTypes.hasOwnProperty(emailType);

export type ProductType = {
  name: string;
  imgUrl: string;
  variantName?: string;
  variantDisplayName?: string;
  quantity?: number;
  price: string;
};

/**
 * An object shaped like this represents a "Price", with both the unit and magnitude.
 *
 * @attribute value {Number} The integer value, in the currency's smallest denomination. (This smallest denomination
 *                           is defined by stripe, and documented in our SharedConfig.js)
 * @attribute unit  {String} An ISO 4217 currency code. We define which ones are available in SharedConfig.js
 */

export type Price = {
  value: number;
  unit: string;
};

export type PriceWithString = {
  value: number;
  unit: string;
  string: string;
};

export type Quantity = {
  value: number;
  unit: string;
};

export type FlatRateShippingRule = {
  amount: Price;
  perItem?: boolean;
};

export type PercentageShippingRule = {
  amount: Quantity;
};

export type RangeShippingRule = {
  amount: Price;
  minValue: Quantity;
  maxValue?: Quantity;
};

export type ShippingMethodMode =
  | typeof SHIPPING_METHODS.FLAT
  | typeof SHIPPING_METHODS.PERCENTAGE
  | typeof SHIPPING_METHODS.PRICE
  | typeof SHIPPING_METHODS.QUANTITY
  | typeof SHIPPING_METHODS.WEIGHT;

export type ShippingMethodRulesType =
  | {
      mode: typeof SHIPPING_METHODS.FLAT;
      rules: Array<FlatRateShippingRule>;
    }
  | {
      mode: typeof SHIPPING_METHODS.PERCENTAGE;
      rules: Array<PercentageShippingRule>;
    }
  | {
      mode:
        | typeof SHIPPING_METHODS.PRICE
        | typeof SHIPPING_METHODS.QUANTITY
        | typeof SHIPPING_METHODS.WEIGHT;
      rules: Array<RangeShippingRule>;
    };

// Flow workaround using $Keys: https://github.com/facebook/flow/issues/961#issuecomment-410557152
// First, we convert the array into an Object that we can use for creating the union with $Keys.
const currencies = stripeCurrencyList.reduce<Record<string, any>>(
  (memo, {code}) => {
    memo[code] = '';
    return memo;
  },
  {}
);
const currenciesPaypal = paypalCurrencyList.reduce<Record<string, any>>(
  (memo, {code}) => {
    memo[code] = '';
    return memo;
  },
  {}
);
const taxCategories = enumOptionMapSets.TaxCategories.reduce<
  Record<string, any>
>((memo, {code}) => {
  memo[code] = '';
  return memo;
}, {});
// Opaque type removed - see /packages/utilities/type-safety/README.md for more information
export type ValidCurrenciesType = keyof typeof currencies;
// Opaque type removed - see /packages/utilities/type-safety/README.md for more information
export type ValidCurrenciesPaypalType = keyof typeof currenciesPaypal;
// Opaque type removed - see /packages/utilities/type-safety/README.md for more information
export type ValidTaxCategoriesType = keyof typeof taxCategories;
// Opaque type removed - see /packages/utilities/type-safety/README.md for more information
// FIXME: use $Keys<typeof billingMethods>
export type ValidBillingMethodsType = string;

// @ts-expect-error - TS2322 - Type 'string | undefined' is not assignable to type 'string'.
export const defaultBillingMethod: ValidBillingMethodsType =
  enumOptionValueSets.BillingMethods[0];

export type InventoryType =
  | typeof INVENTORY_TYPE_FINITE
  | typeof INVENTORY_TYPE_INFINITE;

export type SafePrice = {
  value: number;
  unit: ValidCurrenciesType;
};

export type OptionCombination = {
  [optionSetId: string]: string /* optionId */;
};

export type FiniteInventory = {
  inventoryType: typeof INVENTORY_TYPE_FINITE;
  quantity: number;
};

export type InfiniteInventory = {
  inventoryType: typeof INVENTORY_TYPE_INFINITE;
  quantity: 0;
};

export type Inventory = FiniteInventory | InfiniteInventory;

export type InventoryWithId = Inventory & {
  _id: string;
};

export type ExtFileRefType = {
  url: string;
  name: string;
};

export type StripeSubscriptionPlanPlatform = 'stripe';
export type StripeSubscriptionPlanInterval = 'day' | 'week' | 'month' | 'year';
export type StripeSubscriptionPlanStatus = 'active' | 'inactive' | 'canceled';

export type SubscriptionPlanField = {
  interval: StripeSubscriptionPlanInterval;
  frequency: number;
  trial: number | null;
  plans: Array<{
    platform: StripeSubscriptionPlanPlatform;
    id: string;
    status: string;
  }>;
};

export type Variant = {
  _id: string | null;
  _archived: boolean;
  _draft: boolean;
  name: string;
  slug: string;
  sku: string | null;
  product: string;
  ['sku-values']: OptionCombination;
  price: SafePrice;
  inventory: Inventory | null;
  ['main-image']: ImageRefType | null;
  weight: number | null;
  width: number | null;
  height: number | null;
  length: number | null;
  ['compare-at-price']: SafePrice | null;
  ['more-images']: Array<ImageRefType | null>;
  ['download-files']: Array<ExtFileRefType | null>;
  ['ec-sku-billing-method']: ValidBillingMethodsType;
  ['ec-sku-subscription-plan']?: SubscriptionPlanField | null;
  ['s3Url']?: string;
};

export type VariantKeyPath = Array<number | string>;

export type VariantValue =
  | string
  | boolean
  | number
  | OptionCombination
  | ImageRefType
  | SafePrice
  | Inventory
  | null;

export type VariantChange = {
  keyPath: VariantKeyPath;
  value: VariantValue;
};

export type ProductCsvImportError = {
  line: number | string;
  message: string;
  category?: string;
};

export type ProductCsvImportResults = {
  counts: {
    products: number;
    skus: number;
    categories: {
      existing: number;
      created: number;
    };
  };
  errors: Array<ProductCsvImportError>;
  startTime?: number;
  importError?: string;
};

export type DiscountCsvImportResults = {
  errors: Array<string>;
  failed: number;
  success: number;
};

export type CustomOrderData = Array<{
  name: string;
  textInput?: string;
  textArea?: string;
  checkbox?: boolean;
}>;

export type DownloadFiles = Array<{
  id: string;
  name: string | null | undefined;
  url: string;
}>;

export type SubscriptionPlan = SubscriptionPlanField;

export type UserItem = {
  addedOn: Date;
  count: number;
  price: Price;
  product: string;
  sku: string;
  subscriptionInterval: string;
  subscriptionFrequency: number;
  subscriptionTrial: number;
  updatedOn: Date;
};

export type CommercePendingOrder = Record<any, any> & {
  _id: string;
  orderId: string;
  userItems: Array<UserItem>;
  get: <T>(path: string | Array<string>) => T;
};

export type SiteCommerce = {
  defaultCurrency: string;
};
export type DiscountStatus =
  | 'disabled'
  | 'limit-reached'
  | 'expired'
  | 'scheduled'
  | 'active';

export type StripeCardBrand =
  | 'amex'
  | 'visa'
  | 'mastercard'
  | 'discover'
  | 'jcb'
  | 'diners'
  | 'unionpay'
  | 'unknown';

export type CommerceAcceptedOrderData = {
  acceptedOn: string;
  allAddresses: Array<{
    type: string;
    addressee?: string;
    city: string;
    country: string;
    line1: string | null | undefined;
    line2: string | null | undefined;
    postalCode: string;
    state: string;
  }>;
  customData: CustomOrderData;
  customerInfo: {
    email: string;
    fullName: string;
  };
  customerPaid: PriceWithString;
  downloadFiles: Array<ExtFileRefType>;
  orderId: string;
  paymentProcessor: 'stripe' | 'paypal';
  paypalDetails: Record<any, any> | null;
  purchasedItems: Array<{
    count: number;
    productName: string;
    productSlug: string;
    rowTotal: PriceWithString;
    shippable: boolean;
    variantImage: {
      fileId: string;
      url: string;
      alt: string | null;
    };
    variantName: string;
    variantPrice: PriceWithString;
    variantSlug: string;
  }>;
  purchasedItemsCount: number;
  refundedOn: string | null;
  shippingProvider: string | null;
  shippingTracking: string | null;
  shippingTrackingURL: string | null;
  status: 'fulfilled' | 'unfulfilled';
  stripeCard: {
    brand: StripeCardBrand;
    expires: {
      month: number;
      year: number;
    };
    last4: string;
  };
  stripeDetails: {
    chargeId: string;
  };
  totals: {
    extras: Array<{
      name: string;
      price: PriceWithString;
      type: string;
    }>;
    subtotal: PriceWithString;
    total: PriceWithString;
  };
};

export type Property = {
  id: string;
  name: string;
  enum: Array<PropertyEnum>;
};

export type PropertyEnum = {
  id: string;
  name: string;
  slug: string;
};

export type PropTable = Array<Property>;

export type SkuPropertiesRecordType = List<
  RecordOf<{
    id: string;
    name: string;
    enum: List<RecordOf<PropertyEnum>>;
  }>
>;

export type ProductItem = CMSItemPublic & {
  description?: string;
  ['default-sku']: string;
  ['sku-properties']: PropTable;
  ['tax-category']: string;
  shippable: boolean;
  ['ec-product-type']: string;
  category?: Array<string>;
  hasCorruptVariants?: boolean;
};

export type SkuItemTypes = {
  product: string;
  ['sku-values']: {
    [key: string]: string;
  };
  weight: number;
  price: {
    value: number;
    unit: string;
  };
  ['main-image']?: {
    url: string;
  };
  ['download-files']?: DownloadFiles;
  ['ec-sku-billing-method']?: ValidBillingMethodsType;
  ['ec-sku-subscription-plan']?: SubscriptionPlan;
  ['default-sku']?: string;
};

export type SkuItem = CMSItemPublic & SkuItemTypes;

export type SkuItemInCart = SkuItem & {
  category: Array<string>;
  productType: string;
};

type AppliesTo = {
  scope: string;
  filter: List<string>;
  applyOnce: boolean;
};

export type DiscountPropertyTypes = {
  _id: string;
  archived: boolean;
  active: boolean;
  appliesTo: Map<string, AppliesTo[keyof AppliesTo]>;
  createdOn: string | null | undefined;
  site: string;
  name: string;
  notes: string;
  code: string;
  amountOff:
    | {
        unit: string;
        value: number;
      }
    | null
    | undefined;
  percentOff: number | null | undefined;
  maxAmountOff:
    | {
        unit: string;
        value: number;
      }
    | null
    | undefined;
  updatedOn: string | null | undefined;
  validOn: string | null | undefined;
  expiresOn: string | null | undefined;
  neverExpires: boolean;
  type: 'PERCENTAGE' | 'FIXED_AMOUNT';
  usage: {
    limit: {
      total: number;
      customer: number;
    };
  };
  orderMinimum:
    | {
        unit: string;
        value: number;
      }
    | null
    | undefined;
  totalUsage: number;
};

export type PartialDiscount = Partial<DiscountPropertyTypes>;
export type Discount = RecordOf<DiscountPropertyTypes>;
export type Discounts = List<RecordOf<DiscountPropertyTypes>>;

// Opaque type removed - see /packages/utilities/type-safety/README.md for more information
// Access values of this type using `getOptionCombinationHash`
export type OptionCombinationHash = string;
export type VariantsByOptionCombinationHash = Map<
  OptionCombinationHash,
  Variant
>;

export type ItemMetaType = {
  quantity: number;
  inventoryType: InventoryType;
  variants: VariantsByOptionCombinationHash;
  defaultSkuId: string | null;
};

export type ProductItemStateType = {
  currentItemMeta: ItemMetaType;
  savedCurrentItemMeta: ItemMetaType;
  currentInlineItemMeta: ItemMetaType;
};

export type ProductPickerStateShape = {
  products: List<string>;
  categoryId: string;
};

export type ProductPickerStateType = RecordOf<ProductPickerStateShape>;

export const ProductPickerState: IRecord.Factory<ProductPickerStateShape> =
  IRecord({
    products: List(),
    categoryId: '',
  });
